<template>
    <div>
        <div class="container-fluid mt-45">
            <Breadcrumbs />

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body text-sm">
                            <h5 class="mb-0">Patient Details</h5>
                            
                            <div v-if="!isTrial" class="row justify-content-between mt-4">
                                <div class="col-lg-6 pr-lg-6">
                                    <div class="form-group row">
                                        <label for="identifier" class="col-sm-4">{{ patientIdentifierLabel }} <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="d-flex align-items-center">
                                                <input @focusout="handlePatientIdentifierFocusOut()" :aria-busy="String(isPatientIdentifierBusy)" type="text" class="form-control" :class="{'border border-danger' : errors && errors.identifier}" id="identifier" v-model="form.identifier" autocomplete="new-password">
                                                <span v-if="isPatientIdentifierBusy" class="span-loading ml-2" role="status"><span class="sr-only">Checking {{ patientIdentifierLabel }}</span></span>
                                            </div>
                                            <small v-if="errors && errors.identifier" class="text-danger">
                                                {{ errors.identifier[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="title" class="col-sm-4">Title <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <select :disabled="isBusy" type="text" class="custom-select" :class="{'border border-danger' : errors && errors.title}" id="title" v-model="form.title" autocomplete="new-password">
                                                <option :value="Salutation.MR">Mr</option>
                                                <option :value="Salutation.MRS">Mrs</option>
                                                <option :value="Salutation.MISS">Miss</option>
                                                <option :value="Salutation.MS">Ms</option>
                                                <option :value="Salutation.DR">Dr</option>
                                                <option :value="Salutation.SIR">Sir</option>
                                                <option :value="Salutation.PROFESSOR">Professor</option>
                                            </select>
                                            <small v-if="errors && errors.title" class="text-danger">
                                                {{ errors.title[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="first-name" class="col-sm-4">First name <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" :class="{'border border-danger' : errors && errors.firstName}" id="first-name" v-model="form.firstName" autocomplete="new-password">
                                            <small v-if="errors && errors.firstName" class="text-danger">
                                                {{ errors.firstName[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="middle-names" class="col-sm-4">Middle names</label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" id="middle-names" v-model="form.middleNames" autocomplete="new-password">
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="surname" class="col-sm-4">Surname <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" :class="{'border border-danger' : errors && errors.surname}" id="surname" v-model="form.surname" autocomplete="new-password">
                                            <small v-if="errors && errors.surname" class="text-danger">
                                                {{ errors.surname[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="dob" class="col-sm-4">DOB <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>

                                        <div class="col-sm-8">
                                            <div class="row mb-1">
                                                <div class="col-sm-4">
                                                    <select :disabled="isBusy" class="custom-select" :class="{'border border-danger' : errors && errors.dayOfBirth}" v-model="form.dayOfBirth">
                                                        <option disabled value="null">Day</option>
                                                        <option v-for="item in 31" :key="item" :value="item">{{ item }}</option>
                                                    </select>
                                                </div>

                                                <div class="col-sm-4 mt-1 mt-md-0">
                                                    <select :disabled="isBusy" class="custom-select" :class="{'border border-danger' : errors && errors.monthOfBirth}" v-model="form.monthOfBirth">
                                                        <option disabled value="null">Month</option>
                                                        <option value="1">January</option>
                                                        <option value="2">February</option>
                                                        <option value="3">March</option>
                                                        <option value="4">April</option>
                                                        <option value="5">May</option>
                                                        <option value="6">June</option>
                                                        <option value="7">July</option>
                                                        <option value="8">August</option>
                                                        <option value="9">September</option>
                                                        <option value="10">October</option>
                                                        <option value="11">November</option>
                                                        <option value="12">December</option>
                                                    </select>
                                                </div>

                                                <div class="col-sm-4 mt-1 mt-md-0">
                                                    <select :disabled="isBusy" class="custom-select" :class="{'border border-danger' : errors && errors.yearOfBirth}" v-model="form.yearOfBirth">
                                                        <option disabled value="null">Year</option>
                                                        <option v-for="item in years" :key="item" :value="item">{{ item }}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <small v-if="errors && errors.dayOfBirth" class="text-danger d-block">
                                                {{ errors.dayOfBirth[0] }}
                                            </small>

                                            <small v-if="errors && errors.monthOfBirth" class="text-danger d-block">
                                                {{ errors.monthOfBirth[0] }}
                                            </small>

                                            <small v-if="errors && errors.yearOfBirth" class="text-danger d-block">
                                                {{ errors.yearOfBirth[0] }}
                                            </small>

                                            <small v-if="errors && errors.dateOfBirth" class="text-danger d-block">
                                                {{ errors.dateOfBirth[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="phone-no" class="col-sm-4">Phone number <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" :class="{'border border-danger' : errors && errors.phone}" id="phone-no" v-model="form.phone" autocomplete="new-password">
                                            <small v-if="errors && errors.phone" class="text-danger">
                                                {{ errors.phone[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label for="email" class="col-sm-4">Email <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" :class="{'border border-danger' : errors && errors.email}" id="email" v-model="form.email" autocomplete="new-password">
                                            <small v-if="errors && errors.email" class="text-danger">
                                                {{ errors.email[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <div>
                                        <template v-if="organisationCountryCode == 'GB'">
                                            <UkAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :isBusy="isBusy"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="organisationCountryCode == 'US'">
                                            <UsAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :isBusy="isBusy"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="organisationCountryCode == 'QA'">
                                            <QaAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :isBusy="isBusy"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="organisationCountryCode == 'AE'">
                                            <AeAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :isBusy="isBusy"
                                            />

                                            <hr />
                                        </template>

                                        <template v-if="organisationCountryCode == 'SG'">
                                            <SgAddressComponent
                                                :labelColSize="Number(4)"
                                                :inputColSize="Number(8)"
                                                :isBusy="isBusy"
                                            />

                                            <hr />
                                        </template>
                                    </div>
                                </div>

                                <div class="col-lg-6 pl-lg-6">
                                    <div class="form-group row mb-0">
                                        <label for="height" class="col-sm-4 pt-0">Height <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="row">
                                                <div class="col-sm-4">
                                                    <label for="centimetres">Centimetres</label>
                                                    <input :disabled="isBusy" type="number" onkeypress="return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? null : event.charCode >= 48 && event.charCode <= 57" min="0" class="form-control" :class="{'border border-danger' : errors && errors.height}" id="centimetres" @input="convertCentimetresToFeetAndInches(form.height)" v-model="form.height">
                                                    <small v-if="errors && errors.height" class="text-danger">
                                                        {{ errors.height[0] }}
                                                    </small>
                                                </div>

                                                <div class="col-sm-2 d-flex align-items-center justify-content-center">
                                                    <p class="mb-0 mt-3 font-weight-bold text-sm">OR</p>
                                                </div>

                                                <div class="col-sm-3">
                                                    <label for="feet">Feet</label>
                                                    <input :disabled="isBusy" type="number" min="0" step="1" @input="convertFeetAndInchesToCentimetres(feet, inches, true)" class="form-control" id="feet" v-model="feet">
                                                </div>

                                                <div class="col-sm-3">
                                                    <label for="inches">Inches</label>
                                                    <input :disabled="isBusy" type="number" min="0" max="11" step="1" @input="convertFeetAndInchesToCentimetres(feet, inches, true)" class="form-control" id="inches" v-model="inches">
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <hr>

                                    <div class="form-group row mb-0">
                                        <label for="weight" class="col-sm-4 pt-0">Weight <span v-if="organisationId != organisationIdException" class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="row">
                                                <div class="col-sm-4">
                                                    <label for="kilograms">Kilograms</label>
                                                    <input :disabled="isBusy" type="number" min="0" class="form-control" :class="{'border border-danger' : errors && errors.weight}" id="kilograms" @input="convertKilosToStonesAndPounds(form.weight)" v-model="form.weight">
                                                    <small v-if="errors && errors.weight" class="text-danger">
                                                        {{ errors.weight[0] }}
                                                    </small>
                                                </div>

                                                <div class="col-sm-2 d-flex align-items-center justify-content-center">
                                                    <p class="mb-0 mt-3 font-weight-bold text-sm">OR</p>
                                                </div>

                                                <div class="col-sm-3">
                                                    <label for="stone">Stone</label>
                                                    <input :disabled="isBusy" class="form-control" type="number" min="0" @input="convertStonesAndPoundsToKilos(stones, pounds, true)" v-model="stones" />
                                                </div>

                                                <div class="col-sm-3">
                                                    <label for="pounds">Pounds</label>
                                                    <input :disabled="isBusy" class="form-control" type="number" max="14" min="0" @input="convertStonesAndPoundsToKilos(stones, pounds, true)" v-model="pounds" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <hr>

                                    <div class="form-group row mb-0">
                                        <label for="gender" class="col-sm-4 pt-0">Gender <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="custom-radio">
                                                <div class="form-check">
                                                    <input :disabled="isBusy" class="form-check-input" type="radio" name="genderRadios" value="0" v-model="form.gender">
                                                    <label class="form-check-label">
                                                        Male
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="custom-radio">
                                                <div class="form-check">
                                                    <input :disabled="isBusy" class="form-check-input" type="radio" name="genderRadios" value="1" v-model="form.gender">
                                                    <label class="form-check-label">
                                                        Female
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="custom-radio">
                                                <div class="form-check">
                                                    <input :disabled="isBusy" class="form-check-input" type="radio" name="genderRadios" value="2" v-model="form.gender">
                                                    <label class="form-check-label">
                                                        Other / prefer not to say
                                                    </label>
                                                </div>
                                            </div>
                                            <small v-if="errors && errors.gender" class="text-danger">
                                                {{ errors.gender[0] }}
                                            </small>
                                        </div>
                                    </div>

                                    <hr>

                                    <div class="form-group row mb-0">
                                        <label for="ethnicity" class="col-sm-4 pt-0">Ethnicity group <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="form-group">
                                                <label>Primary ethnicity</label>
                                                <select class="custom-select" :disabled="isBusy" v-model="form.primaryEthnicity" @change="clearEthnicities(true)">
                                                    <option value=null disabled>Please select an option..</option>
                                                    <option v-for="option in ethnicities" :key="option.code" :value="option">{{ option.label }}</option>
                                                </select>
                                                <small v-if="errors && errors.primaryEthnicity" class="text-danger">
                                                    {{ errors.primaryEthnicity[0] }}
                                                </small>
                                            </div>

                                            <div v-if="form.primaryEthnicity != null && form.primaryEthnicity.secondaryEthnicityDefinitions.length" class="form-group">
                                                <label>Secondary ethnicity</label>
                                                <select class="custom-select" :disabled="isBusy" v-model="form.secondaryEthnicity" @change="clearEthnicities(false)">
                                                    <option value=null disabled>Please select an option..</option>
                                                    <option v-for="option in form.primaryEthnicity.secondaryEthnicityDefinitions" :key="option.code" :value="option">{{ option.label }}</option>
                                                </select>
                                                <small v-if="errors && errors.secondaryEthnicity" class="text-danger">
                                                    {{ errors.secondaryEthnicity[0] }}
                                                </small>
                                            </div>

                                            <div v-if="form.primaryEthnicity != null && form.secondaryEthnicity != null && form.secondaryEthnicity.tertiaryEthnicityDefinitions.length" class="form-group">
                                                <label>Tertiary ethnicity</label>
                                                <select class="custom-select" :disabled="isBusy" v-model="form.tertiaryEthnicity">
                                                    <option value=null disabled>Please select an option..</option>
                                                    <option v-for="option in form.secondaryEthnicity.tertiaryEthnicityDefinitions" :key="option.code" :value="option">{{ option.label }}</option>
                                                </select>
                                                <small v-if="errors && errors.tertiaryEthnicity" class="text-danger">
                                                    {{ errors.tertiaryEthnicity[0] }}
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <hr>

                                    <div class="form-group row mb-0">
                                        <label for="preferredlanguage" class="col-sm-4 pt-0">Preferred language <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <div class="form-group">
                                                <select class="custom-select" :disabled="isBusy" id="preferredlanguage" :class="{'border border-danger' : errors && errors.language}" v-model="form.preferredLanguage">
                                                    <option disabled selected value="null">Please select a language</option>
                                                    <option v-for="language in languages" :value="language.code" :key="language.id">{{ language.name }}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="row mt-4">
                                <div class="col-lg-6 pr-lg-6">
                                    <div class="form-group row">
                                        <label for="identifier" class="col-sm-4">{{ patientIdentifierLabel }} <span class="text-danger">*</span></label>
                                        <div class="col-sm-8">
                                            <input :disabled="isBusy" type="text" class="form-control" :class="{'border border-danger' : errors && errors.identifier}" id="identifier" v-model="form.identifier">
                                            <small v-if="errors && errors.identifier" class="text-danger">
                                                {{ errors.identifier[0] }}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>Consent</h5>

                            <div class="row mt-4">
                                <div class="col-lg-6">
                                    <p class="text-sm">The patient will be emailed a link to the privacy policy after creation.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="fixed-bottom bg-white py-25">
            <div class="container-fluid">
                <div class="row">
                    <div class="col text-right center-mobile">
                        <router-link :to="{name: 'Dashboard'}" class="btn btn-cancel btn-lg lg-shadow">Cancel</router-link>
                        <button type="button" @click="confirm()" onclick="this.blur();" class="btn btn-success btn-lg lg-shadow ml-2">Create patient</button>
                        <span v-if="Object.keys(errors).length" class="text-sm d-block text-danger mt-2">There are errors on the page. Please review them and try again</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import ConfirmPatientDetailsModal from '@/components/modals/ConfirmPatientDetailsModalComponent.vue'
import PatientFoundModal from '@/components/modals/PatientFoundModalComponent.vue'
import {IDENTIFIER_CHECK_OUTCOME, createPatientIdentifierCheck, identifierCheckOutcome, normalisePatientIdentifier} from '@/helpers/patientIdentifierCheck'
import {fetchPatientByIdentifier} from '@/helpers/fetchPatientByIdentifier'
import WeightConversionMixin from '../../../Common/mixins/WeightConversionMixin'
import Salutation from '../../../Common/enums/Salutation'
import UkAddressComponent from '../../../Common/components/UkAddressComponent.vue'
import UsAddressComponent from '../../../Common/components/UsAddressComponent.vue'
import QaAddressComponent from '../../../Common/components/QaAddressComponent.vue'
import AeAddressComponent from '../../../Common/components/AeAddressComponent.vue'
import SgAddressComponent from '../../../Common/components/SgAddressComponent.vue'
import AddressComponentValidationMixin from '../../../Common/mixins/AddressComponentValidationMixin'
import {isValidPhoneNumber, phoneNumberTooShortMessage} from '../../../Common/helpers/phoneNumberRules'
export default {
    name: 'CreatePatient',

    components: {
        Breadcrumbs,
        UkAddressComponent,
        UsAddressComponent,
        QaAddressComponent,
        AeAddressComponent,
        SgAddressComponent
    },

    mixins: [
        WeightConversionMixin,
        AddressComponentValidationMixin
    ],

    data() {
        return {
            isBusy: false,
            isPatientIdentifierBusy: false,
            isPatientFoundModalOpen: false,
            isCheckingIdentifier: false,
            organisationIdException: '9cc86b5d-87a7-44d7-a642-c95ada3c229e',
            stones: null,
            pounds: null,
            feet: null,
            inches: null,
            form: {
                phoneCountryCode: null,
                phone: null,
                email: null,
                dateOfBirth: null,
                dayOfBirth: null,
                monthOfBirth: null,
                yearOfBirth: null,
                identifier: null,
                ethnicity: null,
                primaryEthnicity: null,
                secondaryEthnicity: null,
                tertiaryEthnicity: null,
                preferredLanguage: 'en'
            },
            errors: {},
            ConfirmPatientDetailsModal,
            PatientFoundModal,
            Salutation,
            addressTimeout: null
        }
    },

    created() {
        this.checkPatientIdentifier = createPatientIdentifierCheck(identifier => fetchPatientByIdentifier(this.organisationId, identifier))
    },

    mounted() {
        if(this.myOrganisationSettings.defaultLanguageISOCode != null && this.myOrganisationSettings.defaultLanguageISOCode != 'en') this.form.preferredLanguage = this.myOrganisationSettings.defaultLanguageISOCode
        this.setCountryCodeInAddressStore(this.organisationCountryCode)
    },

    computed: {
        ...mapGetters({
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings',
            ethnicities: 'EthnicityStore/GetEthnicities',
            organisationId: 'UserStore/GetUserOrganisationId',
            organisationCountryCode: 'OrganisationStore/GetMyOrganisationCountryCode',
            isTrial: 'OrganisationStore/GetIsTrial',
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            patientIdentifierRegex: 'OrganisationStore/GetPatientIdentifierRegex',
            patientIdentifierErrorMessage: 'OrganisationStore/GetPatientIdentifierErrorMessage',
            languages: 'LanguageStore/GetLanguages',
            address: 'AddressFormStore/GetAddress',
            addressErrors: 'AddressFormStore/GetAddressErrors'
        }),

        years() {
            var currentYear = new Date().getFullYear(), 
            years = [], 
            startYear = currentYear; 

            while ( startYear >= 1920 ) {
                years.push(startYear--);
            }   

            return years;
        },
    },

    methods: {
        ...mapActions({
            getMyOrganisation: 'OrganisationStore/GetMyOrganisation',
            setNotification: 'NotificationStore/SetNotification',
            setCountryCodeInAddressStore: 'AddressFormStore/SetCountryCode',
            setAddressInAddressStore: 'AddressFormStore/SetAddress'
        }),

        async handlePatientIdentifierFocusOut() {
            if(this.form.identifier != null && this.form.identifier != '' && this.form.identifier.match(this.patientIdentifierRegex)) {
                // checkForm saves the identifier normalised, so focusout must look up that same value to share confirm()'s check.
                const identifier = normalisePatientIdentifier(this.form.identifier)
                try {
                    const patient = await this.getPatientByIdentifier(identifier)
                    if(identifierCheckOutcome(identifier, this.form.identifier, !!patient) === IDENTIFIER_CHECK_OUTCOME.DUPLICATE) this.showPatientFoundModal(patient)
                } catch(error) {
                    console.error('Patient identifier check on focusout failed', error)
                }
            }
        },

        async getPatientByIdentifier(identifier) {
            this.isBusy = true
            this.isPatientIdentifierBusy = true

            try {
                return await this.checkPatientIdentifier(identifier)
            } finally {
                this.isBusy = false
                this.isPatientIdentifierBusy = false
            }
        },

        // Focusout and confirm() both find the same patient when the field is left by clicking 'Create patient'; the flag stops them stacking two modals.
        showPatientFoundModal(patient) {
            if(this.isPatientFoundModalOpen) return
            this.isPatientFoundModalOpen = true
            this.$modal.show(PatientFoundModal, {patient}, {}, {closed: () => { this.isPatientFoundModalOpen = false }})
        },

        clearEthnicities(clearAll) {
            if(clearAll) {
                this.form.secondaryEthnicity = null
                this.form.tertiaryEthnicity = null
            } else {
                this.form.tertiaryEthnicity = null
            }
        },

        async confirm() {
            if(this.isCheckingIdentifier) return

            if (!this.isTrial) {
                this.expandManualAddress()
                this.checkAddress()
            }
            this.checkForm()

            if (Object.keys(this.errors).length || Object.keys(this.addressErrors).length) return
            // On an unexpected failure carry on, as a failed lookup does: the API rejects a duplicate on save.
            try {
                if (!this.isTrial && !await this.isIdentifierClearToConfirm()) return
            } catch(error) {
                console.error('Patient identifier check before confirm failed', error)
            }

            this.setDateOfBirthFromParts()
            if(this.isTrial) this.applyTrialPatientDefaults()

            this.$modal.show(ConfirmPatientDetailsModal, {patient: this.form, stones: this.stones, pounds: this.pounds, feet: this.feet, inches: this.inches, updateMode: false})
        },

        // False stops confirm(): the identifier is a duplicate, or changed during the lookup and has been confirmed again.
        async isIdentifierClearToConfirm() {
            const identifier = this.form.identifier
            this.isCheckingIdentifier = true
            let patient
            try {
                patient = await this.getPatientByIdentifier(identifier)
            } finally {
                this.isCheckingIdentifier = false
            }

            const outcome = identifierCheckOutcome(identifier, this.form.identifier, !!patient)
            if(outcome === IDENTIFIER_CHECK_OUTCOME.RECHECK) {
                await this.confirm()
                return false
            }
            if(outcome === IDENTIFIER_CHECK_OUTCOME.DUPLICATE) {
                this.showPatientFoundModal(patient)
                return false
            }
            return true
        },

        setDateOfBirthFromParts() {
            if(this.form.yearOfBirth != null && this.form.monthOfBirth && this.form.dayOfBirth) {
                let date = new Date(Date.UTC(this.form.yearOfBirth, (this.form.monthOfBirth - 1), this.form.dayOfBirth, 0 ,0 ,0, 0))
                this.form.dateOfBirth = date.toUTCString()
            } else {
                this.form.dateOfBirth = null
            }
        },

        applyTrialPatientDefaults() {
            this.form.title = 3
            this.form.firstName = 'Trial'
            this.form.middleNames = null
            this.form.surname = 'Patient'
            this.form.dateOfBirth = new Date(Date.UTC(2000, 0, 1, 0, 0, 0, 0)).toUTCString()
            this.form.phone = '555'
            this.form.email = 'trial_patient@example.net'
            this.form.height = 170
            this.form.weight = 63.56
            this.form.gender = 1
            this.form.ethnicity = 9
            this.stones = 10
            this.pounds = 0
            this.feet = 5
            this.inches = 5

            this.setAddressInAddressStore({
                buildingNumber: '1',
                line: '1',
                line2: 'Trial Street',
                city: 'Trial',
                provinceName: 'Trial Province',
                postalCode: 'TR1ALL',
                country: 'England',
                countryCode: 'GB',
                latitude: 0,
                longitude: 0
            })
        },

        checkForm() {
            this.errors = {}

            if(this.form.identifier != null) {
                this.form.identifier = normalisePatientIdentifier(this.form.identifier)
            }
            
            if(this.form.phone != null) {
                this.form.phone = this.form.phone.replace(/\s+/g, "")
            }

            if(!this.isTrial && (this.form.title == null || this.form.title < 0)) {
                this.errors.title = ['The title field is required']
            }

            if(!this.isTrial && !this.form.firstName) {
                this.errors.firstName = ['The first name field is required']
            }

            if(!this.isTrial && !this.form.surname) {
                this.errors.surname = ['The surname field is required']
            }

            if(!this.isTrial && this.organisationId != this.organisationIdException && !this.form.dayOfBirth) {
                this.errors.dayOfBirth = ['The day of birth field is required']
            }

            if(!this.isTrial && this.organisationId != this.organisationIdException && !this.form.monthOfBirth) {
                this.errors.monthOfBirth = ['The month of birth field is required']
            }

            if(!this.isTrial && this.organisationId != this.organisationIdException && !this.form.yearOfBirth) {
                this.errors.yearOfBirth = ['The year of birth field is required']
            }

            if(!this.isTrial && this.organisationId != this.organisationIdException && this.form.dayOfBirth && this.form.monthOfBirth && this.form.yearOfBirth) {
                let date = new Date(this.form.yearOfBirth, (this.form.monthOfBirth - 1), this.form.dayOfBirth, 0 ,0 ,0, 0)
                
                if(this.$moment(date).isSameOrAfter()) {
                    this.errors.dateOfBirth = ['This date cannot be in the future']
                }
            }

            if(!this.form.identifier) {
                this.errors.identifier = [`${this.patientIdentifierErrorMessage}`]
            }

            if(this.form.identifier != null && !this.form.identifier.match(this.patientIdentifierRegex)) {
                this.errors.identifier = [`${this.patientIdentifierErrorMessage}`]
            }

            if(!this.isTrial && !this.form.phone) {
                this.errors.phone = ['The phone number field is required']
            }

            if(!this.isTrial && this.form.phone != null && !isValidPhoneNumber(this.form.phone, this.organisationCountryCode)) {
                this.errors.phone = [phoneNumberTooShortMessage(this.organisationCountryCode)]
            }

            if(!this.isTrial && !this.form.email) {
                this.errors.email = ['The email field is required']
            }

            if(!this.isTrial && this.form.email != null && !this.form.email.toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)) {
                this.errors.email = ['This not a valid email address']
            }
            
            if(!this.isTrial && this.organisationId != this.organisationIdException && this.form.height == null || this.form.height < 50 || this.form.height > 250) {
                this.errors.height = ['The height field must be between 50 & 250cm']
            }

            if(!this.isTrial && this.organisationId != this.organisationIdException && (this.form.weight == null || this.form.weight < 31.75 || this.form.weight > 254)) {
                this.errors.weight = ['The weight field must be between 31.75 & 254kg']
            }

            if(!this.isTrial && this.form.gender == null || this.form.gender < 0) {
                this.errors.gender = ['The gender field is required']
            }
            
            if(!this.isTrial && this.form.primaryEthnicity == null) {
                this.errors.primaryEthnicity = ['The primary ethnicity field is required']
            }

            if(!this.isTrial && this.form.primaryEthnicity != null && this.form.primaryEthnicity.secondaryEthnicityDefinitions && this.form.primaryEthnicity.secondaryEthnicityDefinitions.length && this.form.secondaryEthnicity == null) {
                this.errors.secondaryEthnicity = ['The secondary ethnicity field is required']
            }

            if(!this.isTrial && this.form.secondaryEthnicity != null && this.form.secondaryEthnicity.tertiaryEthnicityDefinitions && this.form.secondaryEthnicity.tertiaryEthnicityDefinitions.length && this.form.tertiaryEthnicity == null) {
                this.errors.tertiaryEthnicity = ['The tertiary ethnicity field is required']
            }

            if(Object.keys(this.errors).length) {
                return false
            }

            return true
        }
    }
}
</script>